import { z } from "zod";
import { apiClient, useApiQuery, type Api } from "../../services/api";
import { localStore } from "../../lib/store";
import Loader from "../../components/Loader";
import { displayToast } from "../../components/app/AppToast";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { shareUrl } from "../../domains/app/utils/shareUrl";
import {
  gaEventVisualAbstractArticleClicked,
  gaEventVisualAbstractPublicOpened,
  gaEventVisualAbstractPublicShared,
  gaEventVisualAbstractSignupClicked,
  gaEventVisualAbstractUsefulnessAnswered,
} from "../../tools/analytics/domains/article.analytics";
import { hasLoggedInStore } from "../../domains/auth/stores/deferredDeepLinkStore";
import { recordSharedContentLanding } from "../../tools/analytics/shareAttribution";
import { VisualAbstract } from "./VisualAbstract";

/**
 * This browser's own id for counting poster opens — a random value it mints for
 * itself, stored locally, sent to Juisci's own API and nowhere else.
 *
 * It exists because this page is the ONE surface Mixpanel cannot be run on: it
 * is a capability URL, and an autocapturing SDK here would ship the share token
 * to the vendor (`.knowledge/mixpanel-capability-url-leak.md`). So the open is
 * reported server-side, and the server needs something to tell two readers of
 * the same poster apart from one reader opening it twice.
 *
 * It is deliberately NOT an identity: nothing is derived from the token, the
 * page, or the reader, it is never sent to a third party from here, and it is
 * never aliased onto a Juisci account — a reader who later signs up is
 * attributed through the `utm_source` on the link they followed out of here
 * (the article link, or the signup nudge), never through this.
 *
 * Colocated rather than given a module of its own: this page is the only thing
 * that will ever mint or read it.
 */
const visualAbstractReaderIdStore = localStore<string | null>(
  "visual-abstract-reader",
  null,
  z.string().min(16).max(64).nullable()
);

function visualAbstractReaderId(): string {
  const existing = visualAbstractReaderIdStore.get();
  if (existing !== null) return existing;
  // `crypto.randomUUID` needs a secure context, which a webview or an http dev
  // host is not — and a poster must never fail to render over an analytics id.
  const minted =
    typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  visualAbstractReaderIdStore.set(minted);
  return minted;
}

const referrerUrlSchema = z.url();

/** The referring HOST, never the full URL — that could be another capability. */
function referrerHost(): string | null {
  const parsed = referrerUrlSchema.safeParse(document.referrer);
  return parsed.success ? new URL(parsed.data).host : null;
}

/**
 * Where the signup nudge sends a reader who has no account.
 *
 * Exported only so a test can assert what must never regress: this URL leaves a
 * capability page for the ordinary app shell, so it names the artifact by its
 * ID and carries no share token anywhere — not in the path, not in a `utm_*`
 * (`.knowledge/mixpanel-capability-url-leak.md`). A comment cannot enforce that;
 * a test can.
 *
 * `utm_source` matches the article link so both paths aggregate under one
 * channel, and `utm_medium` is what tells them apart.
 */
export function visualAbstractSignupUrl(artifactId: string): string {
  return (
    `/signup?utm_source=visual_abstract&utm_medium=poster_signup` +
    `&utm_campaign=visual_abstract_v1&utm_content=${encodeURIComponent(artifactId)}`
  );
}

async function reportLinkOpened(token: string): Promise<void> {
  try {
    await apiClient.post("/webapp/public/visual-abstracts/:token/opens", {
      param: { token },
      json: {
        readerId: visualAbstractReaderId(),
        referrerHost: referrerHost(),
      },
    });
  } catch {
    // Best-effort, and deliberately quiet: the poster is what the reader came
    // for, and a beacon refused by a blocker or an offline device must not take
    // the page down — nor toast a reader about our analytics. The failure is
    // already captured by the api client's `onRequestError`.
  }
}

export default function VisualAbstractPage({
  token,
}: {
  token: string;
}): React.ReactElement {
  const visualAbstractQuery = useApiQuery(
    "/webapp/public/visual-abstracts/:token",
    { param: { token }, staleTime: 0 }
  );

  if (visualAbstractQuery.state === "pending") return <Loader expanded />;
  if (visualAbstractQuery.state === "error") {
    if (
      visualAbstractQuery.status === 400 ||
      visualAbstractQuery.status === 404
    )
      return <VisualAbstractNotFound />;
    throw visualAbstractQuery.error;
  }

  return (
    <ReadyVisualAbstract artifact={visualAbstractQuery.body} token={token} />
  );
}

export function VisualAbstractNotFound(): React.ReactElement {
  return (
    <main className="grid min-h-dvh place-items-center bg-ds-cream p-6 text-center text-ds-mint-950">
      <div>
        <p className="m-0 text-sm font-bold uppercase tracking-wider text-ds-slate-600">
          404
        </p>
        <h1 className="mb-2 mt-3 text-3xl font-bold">
          Visual abstract unavailable
        </h1>
        <p className="m-0 text-ds-slate-600">
          This link does not exist or is no longer shared.
        </p>
      </div>
    </main>
  );
}

function ReadyVisualAbstract({
  artifact,
  token,
}: {
  artifact: Api["get"]["/webapp/public/visual-abstracts/:token"]["output"]["body"];
  token: string;
}): React.ReactElement {
  const view = artifact.view;
  const brandSource = view.branding.source;
  const dimensions = {
    visualAbstractId: artifact.id,
    roomId: artifact.roomId,
    language: view.language,
    family: artifact.family,
    sourceType: artifact.sourceType,
    generatorVersion: artifact.generatorVersion,
    brandSource,
    articleSlug: view.articleSlug,
  };

  useEffectOnce(() => {
    gaEventVisualAbstractPublicOpened(dimensions);
    // A viewer may sign up next: the poster credits its owner, whom the api
    // resolves from this id (`shareAttribution.ts`).
    recordSharedContentLanding({
      kind: "visual_abstract",
      visualAbstractId: artifact.id,
    });
    // The Mixpanel half of the same fact, emitted by the API because this page
    // cannot run Mixpanel. GA4 keeps its own event so the two stay independently
    // verifiable rather than one being the other's only witness.
    void reportLinkOpened(token);
  }, [artifact.id, brandSource]);

  // `utm_content` carries the ARTIFACT id — the join that turns an attributed
  // visit (and any signup behind it) into "this poster brought this person",
  // rather than a campaign-wide total. The share token stays out of it: a
  // capability in a UTM would be logged by every analytics hop the visit
  // crosses (`.knowledge/mixpanel-capability-url-leak.md`).
  const articleUrl = `/post/${encodeURIComponent(view.articleSlug)}?utm_source=visual_abstract&utm_medium=agent_share&utm_campaign=visual_abstract_v1&utm_content=${artifact.id}`;

  // Null for a browser that has logged in before: that reader has an account,
  // and inviting them to make one reads as a product that does not know them.
  // Read off the local flag the router's own reconnect guard already trusts,
  // rather than by asking Firebase — this page is deliberately minimal and
  // outside the app shell, and pulling auth into it is the kind of creep the
  // capability-URL rule exists to prevent.
  //
  // The signup itself is attributed by Mixpanel's first-touch
  // `$initial_utm_source`, set when this navigation lands in the shell.
  const signupUrl = hasLoggedInStore.get()
    ? null
    : visualAbstractSignupUrl(artifact.id);

  const share = async (): Promise<void> => {
    try {
      // Re-share the capability link itself, stripped of any query string the
      // current visit carried, so the recipient opens the same public artifact.
      // `title` stays distinct from `text`: targets that render both print them
      // on consecutive lines, and passing the short title twice shows it twice.
      await shareUrl(
        view.language === "fr" ? "Résumé visuel" : "Visual abstract",
        view.header.shortTitle,
        Promise.resolve(
          `${globalThis.location.origin}${globalThis.location.pathname}`
        )
      );
      gaEventVisualAbstractPublicShared(dimensions);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      displayToast(
        view.language === "fr"
          ? "Le partage a échoué. Veuillez réessayer."
          : "Sharing failed. Please try again.",
        "error"
      );
    }
  };

  return (
    <VisualAbstract
      view={view}
      actions={{
        kind: "public",
        articleUrl,
        // The ARTIFACT id, never the share token: this link leaves the minimal
        // public shell for the ordinary app, where Mixpanel autocapture and
        // session recording would ship the whole URL to a third party
        // (`.knowledge/mixpanel-capability-url-leak.md`). The id grants nothing
        // — it is already in the article link's `utm_content` just below.
        reportUrl: `/visual-abstract/report/${artifact.id}`,
        signupUrl,
        onArticleClick: () => {
          gaEventVisualAbstractArticleClicked(dimensions);
        },
        // Reported exactly as the article click is: a synchronous `dataLayer`
        // push from the anchor's onClick, immediately before the browser leaves
        // this page. The two share their delivery, so they share their
        // reliability and stay comparable.
        onSignupClick: () => {
          gaEventVisualAbstractSignupClicked(dimensions);
        },
        onShare: () => {
          void share();
        },
        onUsefulness: (useful) => {
          gaEventVisualAbstractUsefulnessAnswered({
            ...dimensions,
            surface: "public",
            useful,
          });
        },
      }}
    />
  );
}
